import { Collapse } from '@mui/material'
import clsx from 'clsx'
import { cloneElement, memo, useCallback, useState } from 'react'
import { Link, matchPath, useLocation } from 'react-router-dom'

import { useGetByTenantNoteInprogressCountQuery } from '@/api/rtk-api'
import AddIcon from '@/assets/add.svg?react'
import Logo from '@/assets/logo.svg?react'
import UsersIcon from '@/assets/multiple-users.svg?react'
import ProductIcon from '@/assets/product.svg?react'
import ReportingIcon from '@/assets/reporting.svg?react'
import SettingsIcon from '@/assets/settings.svg?react'
import { Icon, Pill, RotatingChevronIcon, Rule } from '@/components/core-v2'
import { PATH, TENANT } from '@/const'
import { PermissionName, User } from '@/types/user'
import { useUser } from '@/utilities/hooks/useUser'

import classes from './index.module.scss'
import { Loading } from './Loading'

interface Props {
  children: React.ReactNode
  icon: JSX.Element
  matcher?: string
  permissionRequired?: PermissionName
  pillNumber?: number
  to: string
}

const ListItem = ({ children, icon, matcher, permissionRequired, pillNumber, to }: Props) => {
  const { pathname } = useLocation()
  const { data: userData } = useUser()
  const user = new User(userData)
  const isActivePage = matchPath(matcher ?? to, pathname)

  if (!permissionRequired || user.can(permissionRequired)) {
    return (
      <li className={clsx(classes.listItemWrapper, { [classes.active]: isActivePage })}>
        <Link aria-current={isActivePage ? 'page' : undefined} className={classes.listItem} to={to}>
          {cloneElement(icon, { className: classes.icon })}
          {children}
          {typeof pillNumber === 'number' && (
            <Pill className={classes.pill} variant="primary">
              {pillNumber}
            </Pill>
          )}
        </Link>
      </li>
    )
  }

  return null
}

const DropdownListItem = ({
  children,
  permissionRequired,
  pillNumber,
  to
}: Omit<Props, 'icon'>) => {
  const { pathname } = useLocation()
  const { data: userData } = useUser()
  const user = new User(userData)
  const isActivePage = matchPath(to, pathname)

  if (!permissionRequired || user.can(permissionRequired)) {
    return (
      <li
        className={clsx(classes.listItemWrapper, classes.dropdownListItemWrapper, {
          [classes.active]: isActivePage
        })}
        role="none"
      >
        <Link
          aria-current={isActivePage ? 'page' : undefined}
          className={classes.listItem}
          role="menuitem"
          to={to}
        >
          {children}
          {typeof pillNumber === 'number' && (
            <Pill className={classes.pill} variant="primary">
              {pillNumber}
            </Pill>
          )}
        </Link>
      </li>
    )
  }

  return null
}

type Dropdowns = 'userAdmin' | 'productAdmin' | 'backOffice'

export const Navigation = memo(() => {
  const { pathname } = useLocation()
  const [activeDropdown, setActiveDropdown] = useState<Dropdowns>(null)
  const isUserAdminActive =
    activeDropdown === 'userAdmin' || Boolean(matchPath('/users/*', pathname))
  const isProductAdminActive =
    activeDropdown === 'productAdmin' || Boolean(matchPath('/product-admin/*', pathname))
  const isBackOfficeActive =
    activeDropdown === 'backOffice' || Boolean(matchPath('/back-office/*', pathname))

  const onUserAdminClick = useCallback(() => {
    setActiveDropdown(currentlyActiveDropdown =>
      currentlyActiveDropdown === 'userAdmin' ? null : 'userAdmin'
    )
  }, [setActiveDropdown])
  const onProductAdminClick = useCallback(() => {
    setActiveDropdown(currentlyActiveDropdown =>
      currentlyActiveDropdown === 'productAdmin' ? null : 'productAdmin'
    )
  }, [setActiveDropdown])
  const onBackOfficeClick = useCallback(() => {
    setActiveDropdown(currentlyActiveDropdown =>
      currentlyActiveDropdown === 'backOffice' ? null : 'backOffice'
    )
  }, [setActiveDropdown])

  const userQuery = useUser()

  const notificationQuery = useGetByTenantNoteInprogressCountQuery({ tenant: TENANT })

  const isLoading = userQuery.isLoading || (notificationQuery.isLoading && !notificationQuery.data)

  if (isLoading) return <Loading />

  if (userQuery.data) {
    const user = new User(userQuery.data)
    const hasAccessToUserAdmin =
      user.can('organisation.bank.view') || user.can('user.business_unit.view')
    const hasAccessToProductAdmin =
      user.can('product_admin.assets.view') ||
      user.can('product_admin.portfolios.view') ||
      user.can('product_admin.strategies.view') ||
      user.can('product_admin.deposit.view') ||
      user.can('product_admin.loan.view')
    const hasAccessToBackOffice = user.can('backoffice.view')
    return (
      <nav className={classes.root}>
        <Logo className={classes.logo} />
        <Rule className={classes.rule} />
        <ul className={classes.linkList}>
          <ListItem
            icon={<ReportingIcon className={classes.icon} />}
            matcher={'/reports/*'}
            permissionRequired="trade.view"
            pillNumber={0}
            to={PATH.reportsOverview}
          >
            Reports
          </ListItem>
          <ListItem
            icon={<AddIcon />}
            matcher={'/trades/*'}
            permissionRequired="trade.view"
            pillNumber={notificationQuery?.data?.numberPendingTradeApprovals || 0}
            to={PATH.trades}
          >
            Trade Builder
          </ListItem>
          <ListItem
            icon={<AddIcon />}
            matcher="/note-approvals/*"
            permissionRequired="trade.view"
            pillNumber={notificationQuery?.data?.numberPendingNoteApprovals || 0}
            to={PATH.noteApprovals}
          >
            Note Approvals
          </ListItem>
          <ListItem icon={<ProductIcon />} permissionRequired="risk.view" to={PATH.gapRisk}>
            Gap Risk
          </ListItem>
          {hasAccessToUserAdmin && (
            <li
              className={clsx(classes.listItemWrapper, {
                [classes.menuActive]: isUserAdminActive
              })}
            >
              <button
                aria-controls="userAdminMenu"
                aria-expanded={isUserAdminActive}
                aria-haspopup="true"
                className={classes.listItem}
                id="userAdminMenuButton"
                type="button"
                onClick={onUserAdminClick}
              >
                <UsersIcon className={classes.icon} />
                User Admin
                <RotatingChevronIcon
                  classNames={{ root: classes.chevron }}
                  isRotated={isUserAdminActive}
                  rotationDegrees="90"
                />
              </button>
            </li>
          )}

          <Collapse in={isUserAdminActive}>
            <ul
              aria-labelledby="userAdminMenuButton"
              className={classes.dropdown}
              id="userAdminMenu"
              role="menu"
            >
              <DropdownListItem permissionRequired="user.business_unit.view" to={PATH.usersClients}>
                Clients
              </DropdownListItem>
              <DropdownListItem permissionRequired="organisation.bank.view" to={PATH.usersAbsa}>
                Absa Users
              </DropdownListItem>
              <DropdownListItem
                permissionRequired="organisation.portfolio_manager.view"
                to={PATH.portfolioManagers}
              >
                Portfolio Managers
              </DropdownListItem>
            </ul>
          </Collapse>
          {hasAccessToProductAdmin && (
            <li
              className={clsx(classes.listItemWrapper, {
                [classes.menuActive]: isProductAdminActive
              })}
            >
              <button
                aria-controls="productAdminMenu"
                aria-expanded={isProductAdminActive}
                aria-haspopup="true"
                className={classes.listItem}
                id="productAdminMenuButton"
                type="button"
                onClick={onProductAdminClick}
              >
                <SettingsIcon className={classes.icon} />
                Product Admin
                <RotatingChevronIcon
                  classNames={{ root: classes.chevron }}
                  isRotated={isProductAdminActive}
                  rotationDegrees="90"
                />
              </button>
            </li>
          )}
          <Collapse in={isProductAdminActive}>
            <ul aria-labelledby="productAdminMenuButton" className={classes.dropdown} role="menu">
              <DropdownListItem
                permissionRequired="product_admin.assets.view"
                to={PATH.productAdminAssets}
              >
                Assets
              </DropdownListItem>
              <DropdownListItem
                permissionRequired="product_admin.portfolios.view"
                to={PATH.productAdminPortfolios}
              >
                Portfolios
              </DropdownListItem>
              <DropdownListItem
                permissionRequired="product_admin.strategies.view"
                to={PATH.productAdminStrategies}
              >
                Strategies
              </DropdownListItem>
              <DropdownListItem
                permissionRequired="product_admin.deposit.view"
                to={PATH.productAdminDepositInstruments}
              >
                Deposit Instruments
              </DropdownListItem>
              <DropdownListItem
                permissionRequired="product_admin.loan.view"
                to={PATH.productAdminLoanInstruments}
              >
                Loan Instruments
              </DropdownListItem>
            </ul>
          </Collapse>

          {hasAccessToBackOffice && (
            <li
              className={clsx(classes.listItemWrapper, {
                [classes.menuActive]: isBackOfficeActive
              })}
            >
              <button
                aria-controls="backOfficeMenu"
                aria-expanded={isBackOfficeActive}
                aria-haspopup="true"
                className={classes.listItem}
                id="backOfficeMenuButton"
                type="button"
                onClick={onBackOfficeClick}
              >
                <Icon className={classes.icon} name="folder" />
                Back Office
                <RotatingChevronIcon
                  classNames={{ root: classes.chevron }}
                  isRotated={isBackOfficeActive}
                  rotationDegrees="90"
                />
              </button>
            </li>
          )}
          <Collapse in={isBackOfficeActive}>
            <ul aria-labelledby="backOfficeMenuButton" className={classes.dropdown} role="menu">
              <DropdownListItem permissionRequired="backoffice.view" to={PATH.backOfficeFunds}>
                Fund Distributions
              </DropdownListItem>
              <DropdownListItem permissionRequired="backoffice.view" to={PATH.backOfficeOrders}>
                Orders
              </DropdownListItem>
              <DropdownListItem permissionRequired="backoffice.view" to={PATH.backOfficeTaxReports}>
                Tax Reports
              </DropdownListItem>
              <DropdownListItem
                permissionRequired="backoffice.view"
                to={PATH.backOfficeReconciliation}
              >
                Reconciliation
              </DropdownListItem>
              <DropdownListItem
                permissionRequired="backoffice.view"
                to={PATH.backOfficeUnitTransfers}
              >
                Confirm Unit Transfers
              </DropdownListItem>
            </ul>
          </Collapse>
        </ul>
      </nav>
    )
  }

  return null
})
